import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * SortableTh — a standardised sortable <th> for all hand-rolled tables.
 *
 * Usage:
 *   <SortableTh field="vendorName" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
 *     Vendor
 *   </SortableTh>
 *
 * Props:
 *   field      – the sort key string passed to onSort
 *   sortBy     – currently active sort field
 *   sortDir    – 'asc' | 'desc'
 *   onSort     – (field: string) => void
 *   align      – 'left' (default) | 'right' | 'center'
 *   className  – extra <th> classes
 *   children   – column label
 */
export default function SortableTh({
  field,
  sortBy,
  sortDir,
  onSort,
  align = 'left',
  className = '',
  children,
}) {
  const isActive = sortBy === field;

  const SortIcon = isActive
    ? sortDir === 'asc'
      ? ChevronUp
      : ChevronDown
    : ChevronsUpDown;

  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  return (
    <th
      scope="col"
      onClick={() => onSort(field)}
      className={[
        'px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide',
        'cursor-pointer select-none hover:bg-gray-100 transition-colors',
        alignClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span
        className={[
          'inline-flex items-center gap-1',
          align === 'right' ? 'flex-row-reverse justify-end w-full' : '',
          align === 'center' ? 'justify-center' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span>{children}</span>
        <SortIcon
          className={`w-3.5 h-3.5 flex-shrink-0 ${
            isActive ? 'text-indigo-600' : 'text-gray-400'
          }`}
        />
      </span>
    </th>
  );
}
